import { Event } from '@yandex-int/messenger.utils';
import { call, cancel, delay, fork, join, race } from 'typed-redux-saga';

import { getAppInfoReport } from '../../../pages/call/helpers/get-app-info-report';
import { ChannelHolder } from '../../helpers/channelHolder';
import { bind } from '../../helpers/decorators';
import { COUNTERS } from '../../helpers/logger-enums';
import { createLogger } from '../../helpers/loggerFacade';
import { PeerConnectionStats } from '../../helpers/peer-connection-stats';
import { CONNECTION_START_MARK, CONNECTION_TIME_MEASURE } from '../../helpers/performance';
import { FullStatsReport, StatsReport } from '../../helpers/stats';
import type { RoomAgentBandwidth, RoomAgentXGoogleBitrate } from '../config';
import { GeneralErrorReason, HaltReason } from './constants';
import { RoomAgentError, RoomAgentHaltError } from './errors';
import { RecorderManager } from './recorder/RecorderManager';
import {
    findMainVideoSectionMid,
    getMediaSectionCount,
    PayloadTypeOptions,
    transformIncomingSDP,
    transformOutgoingSDP,
} from './sdp';
import { Description, HaltDescriptor, OfferRequest, RtcEvent } from './types';

interface ConstructorParams {
    recorderManager: RecorderManager;
    bandwidth?: RoomAgentBandwidth;
    xGoogleBitrate?: RoomAgentXGoogleBitrate;
    connectingTimeout?: number;
}

const logger = createLogger('room-agent-connection');

export class Connection {
    static PersistentPayloadTypes: PayloadTypeOptions = {
        audio: [
            {
                payload: 110,
                match: {
                    rtp: (item) => item.codec === 'telephone-event' && item.rate === 48000,
                },
            },
            {
                payload: 126,
                match: {
                    rtp: (item) => item.codec === 'telephone-event' && item.rate === 8000,
                },
            },
        ],
    };

    public readonly onRemoteTrack = new Event<{ track: MediaStreamTrack }>();
    public readonly onConnected = new Event<{ sdp: Maybe<string> }>();
    public readonly onHalt = new Event<{ reason: HaltReason; description?: string }>();
    public readonly onStats = new Event<FullStatsReport>();
    public readonly onRawStats = new Event<RTCStatsReport>();
    public readonly onRtcEvent = new Event<RtcEvent>();
    public readonly onOutgoingSdp = new Event<Description>();
    public readonly onIncomingSdp = new Event<Description>();
    private onIceGatheringComplete = new Event<object>();
    // Два канала, т.к. они синглкаст. Один приватный, один публичный.
    public onIceCandidate = new Event<RTCPeerConnectionIceEvent>();
    private _onIceCandidate = new Event<RTCPeerConnectionIceEvent>();
    private connection!: RTCPeerConnection;

    private closed = false;
    private recorderManager: RecorderManager;
    private bandwidth?: RoomAgentBandwidth;
    private xGoogleBitrate?: RoomAgentXGoogleBitrate;
    private statsCollector!: PeerConnectionStats;
    private mainVideoSectionMid: string | null = null;
    // Если не успели установить соединение за n времени, нужно оповестить TRAM
    private connectingTimeout?: number;
    private connectingTimeoutId?: number;

    constructor({ recorderManager, bandwidth, xGoogleBitrate, connectingTimeout }: ConstructorParams) {
        this.bandwidth = bandwidth;
        this.xGoogleBitrate = xGoogleBitrate;

        this.recorderManager = recorderManager;
        this.connectingTimeout = connectingTimeout;

        this.createConnection();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__ROOMAGENT_CONNECTION = this;
    }

    private destroyConnection(): void {
        if (this.statsCollector) {
            this.statsCollector.stopJob();
        }
        if (this.connection) {
            this.connection.removeEventListener('track', this.handleRemoteTrack);
            this.connection.removeEventListener('icegatheringstatechange', this.handleGatheringStateChange);
            this.connection.removeEventListener('iceconnectionstatechange', this.handleIceConnectionStateChange);
            this.connection.removeEventListener('icecandidate', this.handleIceCandidate);
            this.connection.removeEventListener('connectionstatechange', this.handleConnectionStateChange);
            this.connection.removeEventListener('signalingstatechange', this.handleSignalingStateChange);
            this.connection.removeEventListener('negotiationneeded', this.handleNegotiationNeeded);
            this.connection.close();
        }
        this.stopConnectingTimeout();
    }

    private createConnection(): void {
        this.destroyConnection();

        performance.mark(CONNECTION_START_MARK);
        const connection = new RTCPeerConnection({
            iceServers: [
                {
                    urls: ['stun:stun.rtc.yandex.net:3478'],
                },
            ],
            iceCandidatePoolSize: 4,
        });

        this.recorderManager.recorders.forEach((recorder) => connection.addTrack(recorder.getTrack()));

        connection.addEventListener('track', this.handleRemoteTrack);
        connection.addEventListener('icegatheringstatechange', this.handleGatheringStateChange);
        connection.addEventListener('iceconnectionstatechange', this.handleIceConnectionStateChange);
        connection.addEventListener('icecandidate', this.handleIceCandidate);
        connection.addEventListener('connectionstatechange', this.handleConnectionStateChange);
        connection.addEventListener('signalingstatechange', this.handleSignalingStateChange);
        connection.addEventListener('negotiationneeded', this.handleNegotiationNeeded);

        this.connection = connection;
        this.statsCollector = new PeerConnectionStats({
            getPeerConnection: this.getConnection,
            onStats: this.onRtcStats,
            onRawStats: this.onRawRtcStats,
            getParticipantIdByTrackId: this.getParticipantIdByTrackId,
            interval: 5000,
        });
    }

    private stopConnectingTimeout() {
        window.clearTimeout(this.connectingTimeoutId);
        this.connectingTimeoutId = undefined;
    }

    private startConnectingTimeout() {
        if (this.connectingTimeout === undefined || this.connectingTimeout <= 0) {
            return;
        }

        if (this.connectingTimeoutId !== undefined) {
            return;
        }

        this.connectingTimeoutId = window.setTimeout(() => {
            const error = new RoomAgentHaltError(
                HaltReason.NO_INTERNET_CONNECTION,
                `DTLS connecting timeout: ${this.connectingTimeout}`,
                {
                    method: 'connection:connecting-timeout',
                    additional: {
                        timeout: this.connectingTimeout,
                    },
                }
            );
            logger.error(error);
            this.halt({
                reason: error.reason,
                description: error.description,
            });
        }, this.connectingTimeout);
    }

    private isLocalDescriptionValid(localSdp?: string): localSdp is string {
        // Has at least 1 non-local (non-mDNS) candidate. Otherwise, the connection will most likely not happen.
        const isValid = Boolean(
            localSdp?.match(/a=candidate.+\r\n/g)?.some((candidate) => !candidate.includes('.local'))
        );

        if (!isValid) {
            logger.debug('invalid local sdp:', localSdp?.replace(/\r\n/g, '\\n'));
        }

        return isValid;
    }

    private *waitForIceGathering() {
        if (this.connection.iceGatheringState === 'complete') {
            return;
        }

        logger.debug('waiting for ice gathering');

        const holder = new ChannelHolder();
        const timeoutTask = yield* fork(function* () {
            yield* delay(2500);

            return true;
        });

        /**
         * Если есть remote sdp: 1 media section – 1 кандидат
         * Если нет remote sdp (offer required) – смотрим local sdp
         * Если нет ни того, ни другого или не нашли media секции, то делаем fallback на обычный gathering.
         */
        const sdpToFindMediaSectionsIn =
            this.connection.remoteDescription?.sdp || this.connection.localDescription?.sdp;
        const iceCandidatesRequired = getMediaSectionCount(sdpToFindMediaSectionsIn || '');
        const fastGatheringEnabled = iceCandidatesRequired !== null && iceCandidatesRequired > 0;

        if (!fastGatheringEnabled) {
            logger.debug("Can't perform fast-gathering. Falling back to general gathering");
        }

        const iceCandidatesGatheredMids = new Set<string>();

        if (fastGatheringEnabled) {
            logger.debug('fast-gathering. required candidates:', iceCandidatesRequired);
        }

        try {
            while (true) {
                const { timeout, iceGatheringComplete, iceCandidateEvent } = yield* race({
                    timeout: join(timeoutTask),
                    iceGatheringComplete: holder.take(this.onIceGatheringComplete),
                    iceCandidateEvent: holder.take(this._onIceCandidate),
                });

                if (timeout) {
                    this.onRtcEvent.dispatch({ type: 'ice-gathering', kind: 'timeout' });

                    logger.error(
                        new RoomAgentError(GeneralErrorReason.ICE_GATHERING_ERROR, 'ice gathering timeout', {
                            method: 'connection:setupAndSendLocalDescription:ice-gathering',
                        })
                    );
                    return;
                }

                if (
                    fastGatheringEnabled &&
                    iceCandidateEvent?.candidate &&
                    !iceCandidateEvent.candidate.candidate.includes('.local') &&
                    iceCandidateEvent.candidate.sdpMid
                ) {
                    // Пушим значение mid чтобы знать для каких media секций собраны кандидаты
                    iceCandidatesGatheredMids.add(iceCandidateEvent.candidate.sdpMid);
                }

                // Проверяем, что для каждой media секции есть хотя бы 1 ice кандидат
                if (fastGatheringEnabled && iceCandidatesGatheredMids.size === iceCandidatesRequired) {
                    logger.debug('fast-gathering. finished');
                    return;
                }

                if (iceGatheringComplete) {
                    return;
                }
            }
        } finally {
            yield* cancel(timeoutTask);
            logger.debug('ice gathering finished');
            logger.endDelta(COUNTERS.ROOM_AGENT_ICE_GATHERING_DELTA);
            holder.close();
        }
    }

    private *setupAndSendLocalDescription(type: 'offer' | 'answer') {
        logger.debug('setupAndSendLocalDescription with type:', type);

        const description = yield* call(() => {
            if (type === 'offer') {
                return this.connection.createOffer();
            } else {
                return this.connection.createAnswer();
            }
        });

        logger.debug('created local description');

        yield* call(() => {
            if (!description.sdp) {
                throw new RoomAgentHaltError(HaltReason.UNKNOWN_ERROR, 'Description was created without sdp', {
                    method: 'connection:setupAndSendLocalDescription',
                });
            }

            return this.connection.setLocalDescription({
                type,
                sdp: transformOutgoingSDP(description.sdp, {
                    bandwidth: this.bandwidth,
                    h264ProfileLevelIdDescriptors: this.recorderManager.getVideoTrackH264ProfileLevelIdDescriptors(),
                    payloadTypes: Connection.PersistentPayloadTypes,
                }),
            });
        });

        logger.debug('local description set');

        yield* call([this, this.waitForIceGathering]);

        const localSdp = this.connection.localDescription?.sdp;

        logger.event('Local sdp setted up', {
            additional: {
                type,
                localSdp,
                method: 'setupAndSendLocalDescription',
            },
        });

        if (!this.isLocalDescriptionValid(localSdp)) {
            this.onRtcEvent.dispatch({ type: 'sdp', kind: 'validation-error' });

            throw new RoomAgentHaltError(HaltReason.UNKNOWN_ERROR, 'Local sdp is empty or includes 0 ice candidates', {
                method: 'connection:setupAndSendLocalDescription',
            });
        }

        // content нужно добавлять после setLocalDescription, т.к. браузер его сотрет
        const transformedLocalSdp = transformOutgoingSDP(localSdp, {
            contentDescriptors: this.recorderManager.getTrackContentDescriptors(),
        });

        this.onOutgoingSdp.dispatch({ type, sdp: transformedLocalSdp });
    }

    private *setDescription({ type, sdp }: Description) {
        this.onIncomingSdp.dispatch({ type, sdp });

        if (type === 'answer' && this.connection.signalingState === 'stable') {
            // Ignore answer if we're not expecting it. Otherwise RTCPeerConnection.setRemoteDescription will throw.
            return;
        }

        try {
            logger.event('Setting remote description', {
                additional: {
                    type,
                    sdp,
                    method: 'setDescription',
                },
            });
            const transformedRemoteSdp = transformIncomingSDP(sdp, {
                xGoogleBitrate: this.xGoogleBitrate,
                removeApplicationSections: type === 'answer' && !this.connection.remoteDescription,
            });

            this.mainVideoSectionMid = findMainVideoSectionMid(transformedRemoteSdp);

            yield* call(() =>
                this.connection.setRemoteDescription({ type, sdp: transformedRemoteSdp }).catch((error) => {
                    throw new RoomAgentHaltError(HaltReason.INVALID_SDP_FORMAT, error.message, {
                        method: 'connection:setDescription:setRemoteDescription',
                    });
                })
            );

            logger.event('Remote description set', {
                additional: {
                    type,
                    transformedRemoteSdp,
                    method: 'setDescription',
                },
            });

            if (type === 'offer') {
                yield* call(() => this.setupAndSendLocalDescription('answer'));
            }
        } catch (error) {
            const normalizedError = RoomAgentHaltError.from(error, { method: 'connection:setDescription' });

            logger.warn(normalizedError, {
                method: 'setDescription',
            });
            this.halt({
                reason: normalizedError.reason,
                description: normalizedError.description,
            });
        }
    }

    public *connect(incomingEvent: Description | OfferRequest) {
        if (incomingEvent && 'sdp' in incomingEvent && incomingEvent.sdp) {
            yield* call(() => this.setDescription(incomingEvent));
        } else {
            yield* call(() => this.requestOffer());
        }
    }

    private *requestOffer() {
        logger.debug('request offer');

        // Video transceiver was removed when meeting room was put on hold
        if (!this.connection.getTransceivers().find((t) => t.sender.track?.kind === 'video')) {
            // Recreating connection with both video and audio transceivers when returning from hold
            this.createConnection();
        }

        try {
            yield* call(() => this.setupAndSendLocalDescription('offer'));
        } catch (error) {
            const normalizedError = RoomAgentHaltError.from(error, { method: 'connection:requestOffer' });

            logger.warn(normalizedError, {
                method: 'requestOffer',
            });
            this.halt({
                reason: normalizedError.reason,
                description: normalizedError.description,
            });
        }
    }

    @bind
    private handleIceCandidate(event: RTCPeerConnectionIceEvent) {
        logger.debug('ice candidate:', JSON.stringify(event.candidate));

        this._onIceCandidate.dispatch(event);
        this.onIceCandidate.dispatch(event);
    }

    @bind
    private handleGatheringStateChange(event: globalThis.Event) {
        if (!event.target) {
            return;
        }

        const pc = event.target as RTCPeerConnection;

        logger.debug('ice gathering state changed', pc.iceGatheringState);

        if (pc.iceGatheringState === 'gathering') {
            logger.startDelta(COUNTERS.ROOM_AGENT_ICE_GATHERING_DELTA);
        }

        if (pc.iceGatheringState !== 'complete') {
            return;
        }

        this.onIceGatheringComplete.dispatch({});
    }

    @bind
    private handleIceConnectionStateChange(event: globalThis.Event) {
        const pc = event.target as RTCPeerConnection;

        logger.debug('ice connection state change', pc.iceConnectionState);
    }

    @bind
    private handleRemoteTrack(event: RTCTrackEvent) {
        logger.debug('remote track:', event.track.kind, event.track.id);

        const isMainAudioTrack = event.track.kind === 'audio';
        const isMainVideoTrack =
            event.track.kind === 'video' &&
            this.mainVideoSectionMid !== null &&
            event.transceiver.mid === this.mainVideoSectionMid;

        if (isMainAudioTrack || isMainVideoTrack) {
            logger.debug('processing remote track:', event.track.kind, event.track.id);

            this.onRemoteTrack.dispatch({ track: event.track });
        }
    }

    @bind
    private handleSignalingStateChange(event: globalThis.Event) {
        const pc = event.target as RTCPeerConnection;

        logger.debug('signaling state change:', pc.signalingState);
        this.onRtcEvent.dispatch({ type: 'signaling-state', value: pc.signalingState });
    }

    @bind
    private handleNegotiationNeeded() {
        logger.debug('negotiation needed');
    }

    @bind
    private handleConnectionStateChange(event: globalThis.Event) {
        if (!event.target) {
            return;
        }

        const pc = event.target as RTCPeerConnection;

        logger.debug('connection state changed', pc.connectionState);

        this.onRtcEvent.dispatch({ type: 'connection-state', kind: 'rtc', value: pc.connectionState });

        switch (pc.connectionState) {
            case 'connecting':
                this.startConnectingTimeout();
                break;
            case 'connected':
                performance.measure(CONNECTION_TIME_MEASURE, CONNECTION_START_MARK);
                this.stopConnectingTimeout();
                this.onConnected.dispatch({
                    sdp: pc.localDescription?.sdp,
                });

                break;
            case 'disconnected': {
                const roomAgentError = new RoomAgentError(
                    GeneralErrorReason.CONNECTION_ERROR,
                    'connection state error',
                    {
                        method: 'connection:handleConnectionStateChange',
                        additional: {
                            value: pc.connectionState,
                        },
                    }
                );
                logger.error(roomAgentError);
                break;
            }
            case 'closed':
            case 'failed':
                this.halt({
                    reason: HaltReason.NO_INTERNET_CONNECTION,
                    description: `connection was ${pc.connectionState}`,
                });
                break;
        }
    }

    public debugHalt() {
        this.halt({
            reason: HaltReason.NO_INTERNET_CONNECTION,
            description: `Debug halt`,
        });
    }

    private halt(descriptor: HaltDescriptor) {
        if (!this.closed) {
            this.close();

            this.onHalt.dispatch(descriptor);
        }
    }

    @bind
    private getConnection(): RTCPeerConnection {
        return this.connection;
    }

    @bind
    private getParticipantIdByTrackId(trackId: string) {
        if (
            this.recorderManager.recorders.length > 0 &&
            this.recorderManager.recorders.some((recorder) => recorder.getTrack().id === trackId)
        ) {
            return 'room-agent';
        }

        return 'rtp-engine';
    }

    @bind
    private onRtcStats(statsReport: StatsReport) {
        const fullReport: FullStatsReport = {
            standard: statsReport,
            appInfo: getAppInfoReport(),
        };

        this.onStats.dispatch(fullReport);
    }

    @bind
    private onRawRtcStats(statsReport: RTCStatsReport) {
        this.onRawStats.dispatch(statsReport);
    }

    public close() {
        if (this.closed) {
            logger.debug('already terminated');

            return;
        }

        logger.debug('terminating');

        this.closed = true;
        this.destroyConnection();
    }
}
